/**
 * Copy, cut and paste (docs/06-UI-SPEC.md §16).
 *
 * What is held is the work itself, not a pointer to it: a cut piece can still be
 * pasted after it has gone off the board, and work copied on page one can be pasted
 * on page two. Each paste lands a little further down and across than the one before
 * it, so pasting three times gives three copies side by side rather than one pile.
 *
 * The board's own clipboard, and nothing else. Nothing here reaches for the system
 * clipboard, which is what keeps a lesson's ink out of whatever else the teacher has
 * copied today — and their shopping list off the board.
 */

import type { Stroke } from '@opal/shared';

import { NO_EDIT, type BoardEdit } from './board-edit.js';
import { DUPLICATE_OFFSET, type Copied, type Duplicated } from './selection.js';

/** The part of the board a clipboard needs: taking a copy, and putting one down. */
export interface ClipboardBoard {
  /** One piece of the board, lifted off it. Null when it is no longer there. */
  copyOf(strokeId: string): Copied | null;
  /** Puts a held copy back on the board, offset. Null when it could not be placed. */
  place(item: Copied, offset: number): Duplicated | null;
}

/** What the clipboard needs of the tool holding the selection. */
export interface ClipboardHost {
  readonly board: ClipboardBoard;
  /** The work chosen right now. */
  selected(): readonly Stroke[];
  /** Takes the chosen work off the board — the second half of a cut. */
  remove(): void;
  /** Hands the pasted copies to the select tool, and shares what changed. */
  hold(views: readonly Stroke[], edit: BoardEdit): void;
  /** Says that Paste has become worth offering, without the board having changed. */
  announce(): void;
}

/** §16 — the three clipboard actions, and how much is held. */
export interface ClipboardActions {
  copy(): void;
  cut(): void;
  paste(): void;
  /** How many pieces are held, so the panel knows whether to offer Paste. */
  readonly held: number;
}

interface State {
  readonly host: ClipboardHost;
  items: Copied[];
  /** How many times this copy has been pasted, so each one lands clear of the last. */
  pastes: number;
}

/** Remembers the chosen work, in place of whatever was held before. */
function copy(state: State): void {
  state.items = state.host
    .selected()
    .map((stroke) => state.host.board.copyOf(stroke.id))
    .filter((item): item is Copied => item !== null);
  state.pastes = 0;
}

/** The copies as one change to the board: ink as ink, and objects as objects. */
function editFor(copies: readonly Duplicated[]): BoardEdit {
  return {
    ...NO_EDIT,
    added: copies.flatMap((made) => (made.stroke ? [made.stroke] : [])),
    objectsAdded: copies.flatMap((made) => (made.object ? [made.object] : [])),
  };
}

function paste(state: State): void {
  if (state.items.length === 0) return;
  const offset = DUPLICATE_OFFSET * (state.pastes + 1);
  const copies = state.items
    .map((item) => state.host.board.place(item, offset))
    .filter((made): made is Duplicated => made !== null);
  if (copies.length === 0) return;

  // Only a paste that actually landed moves the next one along, so a paste that could
  // not be placed does not leave a gap in the row.
  state.pastes += 1;
  state.host.hold(
    copies.map((made) => made.view),
    editFor(copies),
  );
}

export function clipboardActions(host: ClipboardHost): ClipboardActions {
  const state: State = { host, items: [], pastes: 0 };

  return {
    copy: () => {
      copy(state);
      host.announce();
    },

    // A cut is a copy and then a delete, in that order, so the work is safely held
    // before it leaves the board.
    cut: () => {
      copy(state);
      host.remove();
    },

    paste: () => {
      paste(state);
    },

    get held() {
      return state.items.length;
    },
  };
}
